import { c as _c } from "react/compiler-runtime";
import { Dispatch, ReactNode, SetStateAction, createContext, useContext, useId, useState } from 'react';
import { classes } from '@/uiPrimitives/base/classes.helpers';
import { shelf } from '@/uiPrimitives/layout/shelf';
import { textBlock } from '@/uiPrimitives/typography/text';
import { box } from '@/uiPrimitives/layout/box';
import { indentSize } from '@/uiPrimitives/layout/indentSize.definitions';
import { occupy } from '@/uiPrimitives/layout/occupy';
import { translateInline } from '@/globals/translate/translateInline';
import { unindentInlineEnd } from '../../DecisionTool.css';
import { Collapse } from '@/uiComposites/interactive/Collapse';
import { pile } from '@/uiPrimitives/layout/pile';
import { icon } from '@/uiPrimitives/media/icon';
import { closeIcon } from '@/uiIcons/closeIcon.css';
import { zIndex } from '@/uiPrimitives/layout/zIndex.css';
import { infoCircleIcon } from '@/uiIcons/infoCircleIcon.css';
import { position } from '@/uiPrimitives/layout/position';
import { placement } from '@/uiPrimitives/layout/placement.css';
import { addTestSelector } from '@/globals/testHelpers/testSelector';
import { pushAnswerHelpCollapseEvent, pushAnswerHelpExpandEvent, pushQuiestionHelpCollapseEvent, pushQuiestionHelpExpandEvent } from '@/globals/analytics/events/pushDecisionToolEvent';
const HelpContext = createContext<{
  isOpened: boolean;
  setIsOpened: Dispatch<SetStateAction<boolean>>;
  id: string;
} | undefined>(undefined);
export const useHelp = () => {
  const context = useContext(HelpContext);
  if (context === undefined) {
    throw new Error("useHelp must be used within a HelpProvider");
  }
  return context;
};
const HelpProvider = t0 => {
  const $ = _c(6);
  const {
    children
  } = t0;
  const [isOpened, setIsOpened] = useState(false);
  const id = useId();
  let t1;
  if ($[0] !== id || $[1] !== isOpened) {
    t1 = {
      id,
      isOpened,
      setIsOpened
    };
    $[0] = id;
    $[1] = isOpened;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  let t2;
  if ($[3] !== children || $[4] !== t1) {
    t2 = <HelpContext.Provider value={t1}>{children}</HelpContext.Provider>;
    $[3] = children;
    $[4] = t1;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  return t2;
};
const QuestionHelpButton = t0 => {
  const $ = _c(18);
  const {
    titleText,
    questionId
  } = t0;
  const {
    id,
    isOpened,
    setIsOpened
  } = useHelp();
  let t1;
  if ($[0] !== isOpened || $[1] !== questionId || $[2] !== setIsOpened || $[3] !== titleText) {
    t1 = () => {
      const next = !isOpened;
      setIsOpened(next);
      if (next) {
        pushQuiestionHelpExpandEvent({
          quiestionId: questionId,
          questionTitle: titleText
        });
      } else {
        pushQuiestionHelpCollapseEvent({
          quiestionId: questionId,
          questionTitle: titleText
        });
      }
    };
    $[0] = isOpened;
    $[1] = questionId;
    $[2] = setIsOpened;
    $[3] = titleText;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  const handleClick = t1;
  const t2 = `${isOpened ? "Close" : "Open"} explanation about ${titleText}`;
  const t3 = `${isOpened ? "Sluit" : "Open"} uitleg over ${titleText}`;
  let t4;
  if ($[5] !== t2 || $[6] !== t3) {
    t4 = translateInline({
      en: t2,
      nl: t3
    });
    $[5] = t2;
    $[6] = t3;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  let t5;
  let t6;
  if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = classes(box({
      backgroundColor: "positiveBlue",
      size: "6 | betweenH4Sections"
    }), pile({
      alignItems: "center",
      justifyItems: "center"
    }));
    t6 = addTestSelector("uiHelpButton");
    $[8] = t5;
    $[9] = t6;
  } else {
    t5 = $[8];
    t6 = $[9];
  }
  let t7;
  if ($[10] !== isOpened) {
    t7 = isOpened ? <i aria-hidden={true} className={classes(icon({
      icon: closeIcon,
      blockSize: "1 | h3"
    }))} /> : <i aria-hidden={true} className={classes(textBlock({
      fontWeight: "bold",
      textAlign: "center",
      capSize: "1 | h3"
    }))}>{"?"}</i>;
    $[10] = isOpened;
    $[11] = t7;
  } else {
    t7 = $[11];
  }
  let t8;
  if ($[12] !== handleClick || $[13] !== id || $[14] !== isOpened || $[15] !== t4 || $[16] !== t7) {
    t8 = <button type="button" onClick={handleClick} aria-expanded={isOpened} aria-controls={id} aria-label={t4} className={t5} {...t6}>{t7}</button>;
    $[12] = handleClick;
    $[13] = id;
    $[14] = isOpened;
    $[15] = t4;
    $[16] = t7;
    $[17] = t8;
  } else {
    t8 = $[17];
  }
  return t8;
};
export const AnswerHelpButton = t0 => {
  const $ = _c(19);
  const {
    titleText,
    questionTitle,
    currentQuestionId
  } = t0;
  const {
    id,
    isOpened,
    setIsOpened
  } = useHelp();
  let t1;
  if ($[0] !== currentQuestionId || $[1] !== isOpened || $[2] !== questionTitle || $[3] !== setIsOpened || $[4] !== titleText) {
    t1 = () => {
      const next = !isOpened;
      setIsOpened(next);
      if (next) {
        pushAnswerHelpExpandEvent({
          currentQuestionId,
          answerTitle: titleText,
          questionTitle
        });
      } else {
        pushAnswerHelpCollapseEvent({
          currentQuestionId,
          answerTitle: titleText,
          questionTitle
        });
      }
    };
    $[0] = currentQuestionId;
    $[1] = isOpened;
    $[2] = questionTitle;
    $[3] = setIsOpened;
    $[4] = titleText;
    $[5] = t1;
  } else {
    t1 = $[5];
  }
  const handleClick = t1;
  let t2;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = classes(occupy({
      displayOutside: "inline",
      blockSize: "capSize"
    }));
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  const t3 = `${isOpened ? "Close" : "Open"} explanation about ${titleText}`;
  const t4 = `${isOpened ? "Sluit" : "Open"} uitleg over ${titleText}`;
  let t5;
  if ($[7] !== t3 || $[8] !== t4) {
    t5 = translateInline({
      en: t3,
      nl: t4
    });
    $[7] = t3;
    $[8] = t4;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  let t6;
  if ($[10] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = classes(zIndex({
      zIndex: "1"
    }), position({
      position: "relative"
    }), box({
      borderRadius: "circle"
    }));
    $[10] = t6;
  } else {
    t6 = $[10];
  }
  let t7;
  if ($[11] !== isOpened) {
    t7 = isOpened ? <div className={classes(box({
      backgroundColor: "positiveBlue",
      size: "3 | h1",
      borderRadius: "circle"
    }), pile({
      alignItems: "center",
      justifyItems: "center"
    }))}><i aria-hidden={true} className={classes(icon({
        icon: closeIcon,
        blockSize: "-1 | small"
      }))} /></div> : <i aria-hidden={true} className={classes(icon({
      icon: infoCircleIcon,
      blockSize: "3 | h1",
      fill: "positiveBlue"
    }))} />;
    $[11] = isOpened;
    $[12] = t7;
  } else {
    t7 = $[12];
  }
  let t8;
  if ($[13] !== handleClick || $[14] !== id || $[15] !== isOpened || $[16] !== t5 || $[17] !== t7) {
    t8 = <div className={t2}><button type="button" onClick={handleClick} aria-expanded={isOpened} aria-controls={id} aria-label={t5} className={t6}>{t7}</button></div>;
    $[13] = handleClick;
    $[14] = id;
    $[15] = isOpened;
    $[16] = t5;
    $[17] = t7;
    $[18] = t8;
  } else {
    t8 = $[18];
  }
  return t8;
};
const HelpContent = t0 => {
  const $ = _c(9);
  const {
    children
  } = t0;
  const {
    id,
    isOpened
  } = useHelp();
  let t1;
  let t2;
  let t3;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = addTestSelector("uiHelpContent");
    t2 = classes(box({
      paddingBlockStart: "-1 | small"
    }));
    t3 = classes(box({
      backgroundColor: "negative",
      paddingInline: indentSize,
      paddingBlock: "3 | h1"
    }));
    $[0] = t1;
    $[1] = t2;
    $[2] = t3;
  } else {
    t1 = $[0];
    t2 = $[1];
    t3 = $[2];
  }
  let t4;
  if ($[3] !== children) {
    t4 = <div className={t2}><div className={t3}>{children}</div></div>;
    $[3] = children;
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  let t5;
  if ($[5] !== id || $[6] !== isOpened || $[7] !== t4) {
    t5 = <Collapse id={id} isOpened={isOpened} {...t1}>{t4}</Collapse>;
    $[5] = id;
    $[6] = isOpened;
    $[7] = t4;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  return t5;
};
export const QuestionHelp = t0 => {
  const $ = _c(21);
  const {
    children,
    titleText,
    questionId,
    help
  } = t0;
  if (!help) {
    return children;
  }
  let t1;
  let t2;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = classes(unindentInlineEnd, zIndex({
      isolation: "isolate"
    }));
    t2 = classes(shelf({
      columnGap: "1 | h3",
      gridTemplateColumns: "minmax(0, 1fr) auto"
    }));
    $[0] = t1;
    $[1] = t2;
  } else {
    t1 = $[0];
    t2 = $[1];
  }
  let t3;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = classes(zIndex({
      zIndex: "1"
    }), occupy({
      blockSize: "1 | h3"
    }), placement({
      alignSelf: "end"
    }));
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  let t4;
  if ($[3] !== questionId || $[4] !== titleText) {
    t4 = <QuestionHelpButton titleText={titleText} questionId={questionId} />;
    $[3] = questionId;
    $[4] = titleText;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  let t5;
  if ($[6] !== t3 || $[7] !== t4) {
    t5 = <div className={t3}>{t4}</div>;
    $[6] = t3;
    $[7] = t4;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  let t6;
  if ($[9] !== children || $[10] !== t2 || $[11] !== t5) {
    t6 = <div className={t2}>{children}{t5}</div>;
    $[9] = children;
    $[10] = t2;
    $[11] = t5;
    $[12] = t6;
  } else {
    t6 = $[12];
  }
  let t7;
  if ($[13] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = classes(textBlock({}));
    $[13] = t7;
  } else {
    t7 = $[13];
  }
  let t8;
  if ($[14] !== help || $[15] !== t7) {
    t8 = <HelpContent><div className={t7}>{help}</div></HelpContent>;
    $[14] = help;
    $[15] = t7;
    $[16] = t8;
  } else {
    t8 = $[16];
  }
  let t9;
  if ($[17] !== t1 || $[18] !== t6 || $[19] !== t8) {
    t9 = <HelpProvider><div className={t1}>{t6}{t8}</div></HelpProvider>;
    $[17] = t1;
    $[18] = t6;
    $[19] = t8;
    $[20] = t9;
  } else {
    t9 = $[20];
  }
  return t9;
};
export const AnswerHelp = t0 => {
  const $ = _c(9);
  const {
    children,
    help
  } = t0;
  if (!help) {
    return children;
  }
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = classes(unindentInlineEnd, zIndex({
      isolation: "isolate"
    }));
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  let t2;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = classes(textBlock({}));
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  let t3;
  if ($[2] !== help || $[3] !== t2) {
    t3 = <HelpContent><div className={t2}>{help}</div></HelpContent>;
    $[2] = help;
    $[3] = t2;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  let t4;
  if ($[5] !== children || $[6] !== t1 || $[7] !== t3) {
    t4 = <HelpProvider><div className={t1}>{children}{t3}</div></HelpProvider>;
    $[5] = children;
    $[6] = t1;
    $[7] = t3;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  return t4;
};